import React, { useState, useEffect, useCallback } from 'react';
import saasPlatformService from '../../services/saasPlatformService';
import './SaasPlatform.css';

const PlatformHealth = () => {
  const getInitialTab = () => {
    try {
      if (typeof window !== 'undefined' && window.location && window.location.search) {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tab');
        if (tab && ['fleet', 'incidents', 'deadletter', 'history', 'compatibility'].includes(tab)) {
          return tab;
        }
      }
    } catch { /* non-browser test environment */ }
    return 'fleet';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [health, setHealth] = useState(null);
  const [fleetHealth, setFleetHealth] = useState(null);
  const [fleetCompatibility, setFleetCompatibility] = useState(null);
  const [compatibilityLoading, setCompatibilityLoading] = useState(false);
  const [dryRunSql, setDryRunSql] = useState({
    migrationSql: '-- Disposable Schema Dry-Run\nCREATE TABLE dryrun_verification_test (id INT PRIMARY KEY AUTO_INCREMENT, metric_val VARCHAR(100) NOT NULL);',
    rollbackSql: 'DROP TABLE IF EXISTS dryrun_verification_test;'
  });
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [deadLetters, setDeadLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);

  // Filters for incidents
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [severityFilter, setSeverityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Resolution modal state
  const [resolvingIncident, setResolvingIncident] = useState(null);
  const [resolutionComment, setResolutionComment] = useState('');
  const [resolutionError, setResolutionError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [healthRes, fleetRes, incidentsRes, deadLetterRes] = await Promise.allSettled([
        saasPlatformService.getPlatformHealth(),
        saasPlatformService.getFleetHealth(),
        saasPlatformService.getIncidents({
          status: statusFilter === 'ALL' ? '' : statusFilter,
          severity: severityFilter,
          category: categoryFilter,
          limit: 50
        }),
        saasPlatformService.getDeadLetterDeliveries(50)
      ]);

      if (healthRes.status === 'fulfilled') {
        setHealth(healthRes.value.data || healthRes.value);
      }
      if (fleetRes.status === 'fulfilled') {
        setFleetHealth(fleetRes.value.data || fleetRes.value);
      }
      if (incidentsRes.status === 'fulfilled') {
        setIncidents(incidentsRes.value.data || incidentsRes.value || []);
      }
      if (deadLetterRes.status === 'fulfilled') {
        setDeadLetters(deadLetterRes.value.data || deadLetterRes.value || []);
      }
      setLastCheck(new Date());
    } catch (err) {
      console.error('Failed loading platform resilience data', err);
      setError(err?.response?.data?.message || err?.message || 'Failed connecting to platform resilience services.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, severityFilter, categoryFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAcknowledge = async (incidentId) => {
    setActionLoading(`ack-${incidentId}`);
    setError(null);
    try {
      await saasPlatformService.acknowledgeIncident(incidentId, 'PlatformAdmin', 'Acknowledged via Operations Console');
      setSuccessMessage(`Incident #${incidentId} acknowledged.`);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed acknowledging incident.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenResolve = (incident) => {
    setResolvingIncident(incident);
    setResolutionComment('');
    setResolutionError('');
  };

  const handleConfirmResolve = async () => {
    if (!resolutionComment || !resolutionComment.trim()) {
      setResolutionError('An operator comment is mandatory for manual incident resolution.');
      return;
    }

    setActionLoading(`resolve-${resolvingIncident.incidentId}`);
    setResolutionError('');
    try {
      await saasPlatformService.resolveIncident(resolvingIncident.incidentId, resolutionComment.trim(), 'PlatformAdmin');
      setSuccessMessage(`Incident #${resolvingIncident.incidentId} marked as RESOLVED. Note: Tenant database health requires health check confirmation.`);
      setResolvingIncident(null);
      await loadData();
    } catch (err) {
      setResolutionError(err?.response?.data?.message || err?.message || 'Failed resolving incident.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetryDeadLetter = async (deliveryId) => {
    setActionLoading(`retry-dl-${deliveryId}`);
    setError(null);
    try {
      const res = await saasPlatformService.retryDeadLetter(deliveryId);
      setSuccessMessage(res?.message || `Alert delivery #${deliveryId} re-sent successfully.`);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Retry delivery failed.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSafeRemediate = async (tenantId, action) => {
    setActionLoading(`rem-${tenantId}-${action}`);
    setError(null);
    try {
      const res = await saasPlatformService.remediateTenant(tenantId, action, `Manual remediation '${action}' triggered by operator`);
      setSuccessMessage(res?.message || `Remediation '${action}' executed successfully.`);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || `Remediation '${action}' failed.`);
    } finally {
      setActionLoading(null);
    }
  };

  const loadFleetCompatibility = async () => {
    setCompatibilityLoading(true);
    setError(null);
    try {
      const res = await saasPlatformService.getFleetCompatibility();
      setFleetCompatibility(res.data || res);
    } catch (err) {
      console.error('Failed loading fleet compatibility', err);
      setError(err?.response?.data?.message || err?.message || 'Failed loading fleet stored procedure compatibility.');
    } finally {
      setCompatibilityLoading(false);
    }
  };

  const handleExecuteDryRun = async () => {
    if (!dryRunSql.migrationSql.trim() || !dryRunSql.rollbackSql.trim()) {
      setError('Both Migration SQL and Rollback SQL are required for disposable dry-run verification.');
      return;
    }
    setDryRunLoading(true);
    setDryRunResult(null);
    setError(null);
    try {
      const res = await saasPlatformService.executeMigrationDryRun(dryRunSql);
      setDryRunResult(res.data || res);
      setSuccessMessage('Disposable migration and rollback dry-run executed successfully.');
    } catch (err) {
      setDryRunResult({
        success: false,
        errorMessage: err?.response?.data?.message || err?.message || 'Dry-run execution failed.'
      });
    } finally {
      setDryRunLoading(false);
    }
  };

  const getSeverityBadgeClass = (sev) => {
    switch (sev) {
      case 'CRITICAL': return 'saas-badge saas-badge-suspended';
      case 'HIGH': return 'saas-badge saas-badge-suspended';
      case 'MEDIUM': return 'saas-badge saas-badge-provisioning';
      default: return 'saas-badge saas-badge-active';
    }
  };

  const getStatusBadgeClass = (st) => {
    switch (st) {
      case 'HEALTHY': return 'saas-badge saas-badge-active';
      case 'DEGRADED': return 'saas-badge saas-badge-provisioning';
      case 'ACTIVE': return 'saas-badge saas-badge-suspended';
      case 'ACKNOWLEDGED': return 'saas-badge saas-badge-provisioning';
      case 'RESOLVED': return 'saas-badge saas-badge-active';
      default: return 'saas-badge saas-badge-suspended';
    }
  };

  return (
    <div className="saas-container">
      {/* Header */}
      <div className="saas-header">
        <div className="saas-header-title">
          <h1>
            <span>🛡️</span> Tenant Alerts, Dead-Letter Queue &amp; Remediation
          </h1>
          <p>Autonomous Consecutive-Failure Detection, Resilient Delivery Backoff &amp; Safe Operational Actions</p>
        </div>
        <div className="saas-header-actions">
          <button className="saas-btn saas-btn-primary" onClick={loadData} disabled={loading}>
            {loading ? '⏳ Refreshing...' : '🔄 Refresh Fleet Telemetry'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="saas-error-box" style={{ marginBottom: '1rem' }}>
          <div><strong>Alert:</strong> {error}</div>
        </div>
      )}
      {successMessage && (
        <div className="saas-card" style={{ background: '#f0fdf4', border: '1px solid #86efac', padding: '0.75rem 1.25rem', marginBottom: '1rem', color: '#166534', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>✓ {successMessage}</span>
          <button style={{ background: 'none', border: 'none', color: '#166534', cursor: 'pointer', fontWeight: 'bold' }} onClick={() => setSuccessMessage(null)}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>
        <button
          className={`saas-btn ${activeTab === 'fleet' ? 'saas-btn-primary' : 'saas-btn-secondary'}`}
          onClick={() => {
            setActiveTab('fleet');
            try {
              if (typeof window !== 'undefined' && window.history && window.location) {
                const url = new URL(window.location.href);
                url.searchParams.set('tab', 'fleet');
                window.history.replaceState({}, '', url.toString());
              }
            } catch {}
          }}
        >
          🩺 Fleet Telemetry
        </button>
        <button
          className={`saas-btn ${activeTab === 'incidents' ? 'saas-btn-primary' : 'saas-btn-secondary'}`}
          onClick={() => {
            setActiveTab('incidents');
            try {
              if (typeof window !== 'undefined' && window.history && window.location) {
                const url = new URL(window.location.href);
                url.searchParams.set('tab', 'incidents');
                window.history.replaceState({}, '', url.toString());
              }
            } catch {}
          }}
        >
          🚨 Active Incidents ({incidents.filter(i => i.status !== 'RESOLVED').length})
        </button>
        <button
          className={`saas-btn ${activeTab === 'deadletter' ? 'saas-btn-primary' : 'saas-btn-secondary'}`}
          onClick={() => {
            setActiveTab('deadletter');
            try {
              if (typeof window !== 'undefined' && window.history && window.location) {
                const url = new URL(window.location.href);
                url.searchParams.set('tab', 'deadletter');
                window.history.replaceState({}, '', url.toString());
              }
            } catch {}
          }}
        >
          📬 Dead-Letter Queue ({deadLetters.length})
        </button>
        <button
          className={`saas-btn ${activeTab === 'history' ? 'saas-btn-primary' : 'saas-btn-secondary'}`}
          onClick={() => {
            setActiveTab('history');
            try {
              if (typeof window !== 'undefined' && window.history && window.location) {
                const url = new URL(window.location.href);
                url.searchParams.set('tab', 'history');
                window.history.replaceState({}, '', url.toString());
              }
            } catch {}
          }}
        >
          📜 Recovery History
        </button>
        <button
          className={`saas-btn ${activeTab === 'compatibility' ? 'saas-btn-primary' : 'saas-btn-secondary'}`}
          onClick={() => {
            setActiveTab('compatibility');
            if (!fleetCompatibility) loadFleetCompatibility();
            try {
              if (typeof window !== 'undefined' && window.history && window.location) {
                const url = new URL(window.location.href);
                url.searchParams.set('tab', 'compatibility');
                window.history.replaceState({}, '', url.toString());
              }
            } catch {}
          }}
        >
          🧩 Fleet Schema &amp; Dry-Runs
        </button>
      </div>

      {/* TAB 1: FLEET TELEMETRY */}
      {activeTab === 'fleet' && (
        <div>
          {/* Metrics Grid */}
          <div className="saas-stats-grid" style={{ marginBottom: '1.5rem' }}>
            <div className="saas-stat-card">
              <span className="saas-stat-label">Catalog Status</span>
              <div className="saas-stat-value" style={{ color: fleetHealth?.catalogStatus === 'HEALTHY' ? '#047857' : '#b91c1c' }}>
                ● {fleetHealth?.catalogStatus || health?.catalogStatus || 'UNKNOWN'}
              </div>
              <div className="saas-stat-sub">Latency: {fleetHealth?.catalogLatencyMs ?? health?.catalogLatencyMs ?? '—'} ms</div>
            </div>
            <div className="saas-stat-card">
              <span className="saas-stat-label">Monitored Tenants</span>
              <div className="saas-stat-value" style={{ color: '#0284c7' }}>
                {fleetHealth?.totalMonitoredTenants ?? health?.activeTenantsCount ?? 0}
              </div>
              <div className="saas-stat-sub">Healthy: {fleetHealth?.healthyCount ?? '—'}</div>
            </div>
            <div className="saas-stat-card">
              <span className="saas-stat-label">Unreachable / Outage</span>
              <div className="saas-stat-value" style={{ color: (fleetHealth?.unreachableCount || 0) > 0 ? '#dc2626' : '#64748b' }}>
                {fleetHealth?.unreachableCount ?? 0}
              </div>
              <div className="saas-stat-sub">Invalid Creds: {fleetHealth?.invalidCredentialsCount ?? 0}</div>
            </div>
            <div className="saas-stat-card">
              <span className="saas-stat-label">Schema Drift / Blocked</span>
              <div className="saas-stat-value" style={{ color: (fleetHealth?.schemaDriftCount || 0) > 0 ? '#b45309' : '#64748b' }}>
                {fleetHealth?.schemaDriftCount ?? 0}
              </div>
              <div className="saas-stat-sub">Degraded: {fleetHealth?.degradedCount ?? 0}</div>
            </div>
          </div>

          {/* Tenants Table */}
          <div className="saas-card">
            <div className="saas-card-header">
              <h3>Tenant Database Fleet Status &amp; On-Demand Health</h3>
              <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                Last checked: {lastCheck ? lastCheck.toLocaleTimeString() : '—'}
              </span>
            </div>
            <div className="saas-card-body" style={{ padding: 0 }}>
              <table className="saas-table">
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Database</th>
                    <th>Health Status</th>
                    <th>Latency</th>
                    <th>Schema Version</th>
                    <th>Diagnostics / Error</th>
                    <th style={{ textAlign: 'right' }}>Safe Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fleetHealth?.tenantResults && fleetHealth.tenantResults.length > 0 ? (
                    fleetHealth.tenantResults.map((t) => (
                      <tr key={t.tenantId}>
                        <td>
                          <strong>{t.tenantCode}</strong>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>ID: {t.tenantId}</div>
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{t.databaseName || '—'}</td>
                        <td>
                          <span className={getStatusBadgeClass(t.status)}>
                            ● {t.status}
                          </span>
                        </td>
                        <td>{t.latencyMs} ms</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                          {t.actualSchemaVersion}
                          {t.compatibilityStatus && (
                            <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{t.compatibilityStatus}</div>
                          )}
                        </td>
                        <td style={{ maxWidth: '280px', fontSize: '0.8125rem', color: t.errorMessage ? '#dc2626' : '#64748b' }}>
                          {t.errorMessage || 'All health checks passing cleanly.'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className="saas-btn saas-btn-secondary"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', marginRight: '0.35rem' }}
                            onClick={() => handleSafeRemediate(t.tenantId, 'RECHECK_HEALTH')}
                            disabled={actionLoading === `rem-${t.tenantId}-RECHECK_HEALTH`}
                          >
                            {actionLoading === `rem-${t.tenantId}-RECHECK_HEALTH` ? '⏳' : '🔍 Check'}
                          </button>
                          <button
                            className="saas-btn saas-btn-secondary"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                            onClick={() => handleSafeRemediate(t.tenantId, 'INVALIDATE_CACHE')}
                            disabled={actionLoading === `rem-${t.tenantId}-INVALIDATE_CACHE`}
                            title="Safely evict cached metadata/connection pool"
                          >
                            {actionLoading === `rem-${t.tenantId}-INVALIDATE_CACHE` ? '⏳' : '🧹 Evict Cache'}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                        {loading ? 'Loading fleet telemetry...' : 'No monitored tenant health records found.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: ACTIVE INCIDENTS */}
      {activeTab === 'incidents' && (
        <div>
          {/* Filters */}
          <div className="saas-card" style={{ marginBottom: '1.25rem', padding: '1rem' }}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <label style={{ fontSize: '0.8125rem', fontWeight: 'bold', display: 'block', marginBottom: '0.25rem' }}>Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ padding: '0.4rem 0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="ACKNOWLEDGED">ACKNOWLEDGED</option>
                  <option value="RESOLVED">RESOLVED</option>
                  <option value="ALL">ALL STATUSES</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.8125rem', fontWeight: 'bold', display: 'block', marginBottom: '0.25rem' }}>Severity:</label>
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  style={{ padding: '0.4rem 0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                >
                  <option value="">ALL SEVERITIES</option>
                  <option value="CRITICAL">CRITICAL</option>
                  <option value="HIGH">HIGH</option>
                  <option value="MEDIUM">MEDIUM</option>
                  <option value="LOW">LOW</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.8125rem', fontWeight: 'bold', display: 'block', marginBottom: '0.25rem' }}>Category:</label>
                <input
                  type="text"
                  placeholder="Filter category (e.g. SCHEMA_DRIFT)"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  style={{ padding: '0.4rem 0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1', width: '220px' }}
                />
              </div>
            </div>
          </div>

          {/* Incidents Table */}
          <div className="saas-card">
            <div className="saas-card-header">
              <h3>Deduplicated Operational Incidents &amp; Alerts</h3>
              <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                Transient failures filtered until threshold reached
              </span>
            </div>
            <div className="saas-card-body" style={{ padding: 0 }}>
              <table className="saas-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Tenant</th>
                    <th>Category</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th>Failures</th>
                    <th>First / Last Detected</th>
                    <th>Sanitized Diagnostic Summary</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {incidents && incidents.length > 0 ? (
                    incidents.map((inc) => (
                      <tr key={inc.incidentId}>
                        <td style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>#{inc.incidentId}</td>
                        <td>
                          <strong>{inc.tenantCode}</strong>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>ID: {inc.tenantId}</div>
                        </td>
                        <td><span style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{inc.failureCategory}</span></td>
                        <td>
                          <span className={getSeverityBadgeClass(inc.severity)}>
                            {inc.severity}
                          </span>
                        </td>
                        <td>
                          <span className={getStatusBadgeClass(inc.status)}>
                            {inc.status}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#dc2626' }}>
                          {inc.consecutiveFailureCount}
                        </td>
                        <td style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          <div>1st: {new Date(inc.firstDetectedAt).toLocaleTimeString()}</div>
                          <div>Last: {new Date(inc.lastDetectedAt).toLocaleTimeString()}</div>
                        </td>
                        <td style={{ maxWidth: '320px', fontSize: '0.8125rem' }}>
                          <div style={{ fontWeight: '500', marginBottom: '0.2rem' }}>{inc.title}</div>
                          <div style={{ color: '#475569', wordBreak: 'break-word' }}>{inc.diagnosticSummary}</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.2rem', fontFamily: 'monospace' }}>
                            Corr: {inc.correlationId}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {inc.status === 'ACTIVE' && (
                            <button
                              className="saas-btn saas-btn-secondary"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', marginRight: '0.35rem' }}
                              onClick={() => handleAcknowledge(inc.incidentId)}
                              disabled={actionLoading === `ack-${inc.incidentId}`}
                            >
                              {actionLoading === `ack-${inc.incidentId}` ? '⏳' : '👁️ Ack'}
                            </button>
                          )}
                          {inc.status !== 'RESOLVED' && (
                            <button
                              className="saas-btn saas-btn-success"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              onClick={() => handleOpenResolve(inc)}
                            >
                              ✓ Resolve
                            </button>
                          )}
                          {inc.status === 'RESOLVED' && (
                            <span style={{ fontSize: '0.75rem', color: '#047857' }}>Resolved</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                        {loading ? 'Loading incidents...' : 'No incidents matching selected filter criteria.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: DEAD-LETTER QUEUE */}
      {activeTab === 'deadletter' && (
        <div className="saas-card">
          <div className="saas-card-header">
            <h3>Operator Notification Dead-Letter Queue</h3>
            <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              Notifications exceeding retry limits or encountering non-transient delivery failures
            </span>
          </div>
          <div className="saas-card-body" style={{ padding: 0 }}>
            <table className="saas-table">
              <thead>
                <tr>
                  <th>Delivery ID</th>
                  <th>Incident ID</th>
                  <th>Channel</th>
                  <th>Recipient</th>
                  <th>Retries</th>
                  <th>Failure Reason</th>
                  <th>Idempotency Key</th>
                  <th style={{ textAlign: 'right' }}>Safe Action</th>
                </tr>
              </thead>
              <tbody>
                {deadLetters && deadLetters.length > 0 ? (
                  deadLetters.map((dl) => (
                    <tr key={dl.deliveryId}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>#{dl.deliveryId}</td>
                      <td>#{dl.incidentId}</td>
                      <td><span className="saas-badge saas-badge-provisioning">{dl.channel}</span></td>
                      <td>{dl.recipient || 'platform-ops'}</td>
                      <td>
                        <span style={{ color: '#dc2626', fontWeight: 'bold' }}>{dl.retryCount}</span> / {dl.maxRetries}
                      </td>
                      <td style={{ maxWidth: '300px', fontSize: '0.8125rem', color: '#b91c1c' }}>
                        {dl.lastFailureReason || 'Exhausted retry policy'}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b' }}>
                        {dl.idempotencyKey}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="saas-btn saas-btn-primary"
                          style={{ padding: '0.25rem 0.65rem', fontSize: '0.75rem' }}
                          onClick={() => handleRetryDeadLetter(dl.deliveryId)}
                          disabled={actionLoading === `retry-dl-${dl.deliveryId}`}
                        >
                          {actionLoading === `retry-dl-${dl.deliveryId}` ? '⏳ Retrying...' : '🔁 Retry Delivery'}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                      {loading ? 'Checking dead-letter queue...' : 'Dead-letter queue is empty. All notifications delivered successfully.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 4: RECOVERY HISTORY */}
      {activeTab === 'history' && (
        <div className="saas-card">
          <div className="saas-card-header">
            <h3>Tenant Database Recovery &amp; Alert Resolution Audit Log</h3>
            <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              Historical audit trail of auto-recovered and manually resolved incidents
            </span>
          </div>
          <div className="saas-card-body" style={{ padding: 0 }}>
            <table className="saas-table">
              <thead>
                <tr>
                  <th>Incident ID</th>
                  <th>Tenant</th>
                  <th>Failure Category</th>
                  <th>Resolved At (UTC)</th>
                  <th>Resolved By</th>
                  <th>Resolution Comment / Audit Evidence</th>
                  <th>Correlation ID</th>
                </tr>
              </thead>
              <tbody>
                {incidents.filter(i => i.status === 'RESOLVED').length > 0 ? (
                  incidents.filter(i => i.status === 'RESOLVED').map((i) => (
                    <tr key={i.incidentId}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>#{i.incidentId}</td>
                      <td><strong>{i.tenantCode}</strong> (ID: {i.tenantId})</td>
                      <td>{i.failureCategory}</td>
                      <td style={{ fontSize: '0.8125rem' }}>{i.resolvedAt ? new Date(i.resolvedAt).toLocaleString() : '—'}</td>
                      <td><strong>{i.resolvedBy || 'System / Auto-Recovery'}</strong></td>
                      <td style={{ maxWidth: '350px', fontSize: '0.8125rem', color: '#334155' }}>
                        {i.resolutionComment || 'Auto-resolved upon successful health check recovery.'}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b' }}>
                        {i.correlationId}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                      No resolved incidents in recent history.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 5: FLEET SCHEMA & DISPOSABLE DRY-RUNS */}
      {activeTab === 'compatibility' && (
        <div>
          {/* Compatibility KPI Cards */}
          <div className="saas-stats-grid" style={{ marginBottom: '1.5rem' }}>
            <div className="saas-stat-card">
              <span className="saas-stat-label">Total Catalog SPs</span>
              <div className="saas-stat-value" style={{ color: '#0284c7' }}>
                {fleetCompatibility?.totalTemplateProcedures ?? (compatibilityLoading ? '⏳' : '104')}
              </div>
              <div className="saas-stat-sub">Standard Template Stored Procedures</div>
            </div>
            <div className="saas-stat-card">
              <span className="saas-stat-label">Compliant Tenants</span>
              <div className="saas-stat-value" style={{ color: '#047857' }}>
                {fleetCompatibility?.compliantTenantsCount ?? (compatibilityLoading ? '⏳' : '—')}
              </div>
              <div className="saas-stat-sub">100% Stored Procedure Match</div>
            </div>
            <div className="saas-stat-card">
              <span className="saas-stat-label">Discrepant Tenants</span>
              <div className="saas-stat-value" style={{ color: (fleetCompatibility?.discrepantTenantsCount || 0) > 0 ? '#b91c1c' : '#64748b' }}>
                {fleetCompatibility?.discrepantTenantsCount ?? (compatibilityLoading ? '⏳' : '0')}
              </div>
              <div className="saas-stat-sub">Missing or Extra Stored Procedures</div>
            </div>
            <div className="saas-stat-card">
              <span className="saas-stat-label">Actions</span>
              <div style={{ marginTop: '0.5rem' }}>
                <button
                  className="saas-btn saas-btn-primary"
                  style={{ width: '100%', fontSize: '0.8125rem' }}
                  onClick={loadFleetCompatibility}
                  disabled={compatibilityLoading}
                >
                  {compatibilityLoading ? '⏳ Inspecting Fleet...' : '🔄 Run Full Fleet Audit'}
                </button>
              </div>
            </div>
          </div>

          {/* Tenants Compatibility Table */}
          <div className="saas-card" style={{ marginBottom: '1.5rem' }}>
            <div className="saas-card-header">
              <h3>Tenant Stored Procedure Compatibility Status</h3>
              <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                Audits each tenant database against canonical platform catalog procedures
              </span>
            </div>
            <div className="saas-card-body" style={{ padding: 0 }}>
              <table className="saas-table">
                <thead>
                  <tr>
                    <th>Tenant ID</th>
                    <th>Tenant Code</th>
                    <th>Database</th>
                    <th>Schema Version</th>
                    <th>Procedures</th>
                    <th>Status</th>
                    <th>Drift Details</th>
                  </tr>
                </thead>
                <tbody>
                  {compatibilityLoading ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                        ⏳ Auditing tenant databases across the fleet...
                      </td>
                    </tr>
                  ) : fleetCompatibility?.reports && fleetCompatibility.reports.length > 0 ? (
                    fleetCompatibility.reports.map((r) => (
                      <tr key={r.tenantId}>
                        <td style={{ fontFamily: 'monospace' }}>#{r.tenantId}</td>
                        <td><strong>{r.tenantCode}</strong></td>
                        <td style={{ fontFamily: 'monospace' }}>{r.databaseName}</td>
                        <td>{r.schemaVersion || '01_init'}</td>
                        <td>{r.proceduresCount}</td>
                        <td>
                          <span className={r.isCompliant ? 'saas-badge saas-badge-active' : 'saas-badge saas-badge-suspended'}>
                            ● {r.isCompliant ? 'COMPLIANT' : 'DISCREPANT'}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.8125rem', color: r.isCompliant ? '#047857' : '#b91c1c' }}>
                          {r.isCompliant
                            ? 'All procedures match standard catalog'
                            : `${r.missingProcedures?.length || 0} missing, ${r.extraProcedures?.length || 0} extra`}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                        Click "Run Full Fleet Audit" to inspect stored procedures across all active tenants.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Disposable Migration Dry-Run Sandbox */}
          <div className="saas-card">
            <div className="saas-card-header">
              <h3>🧪 Disposable Migration Dry-Run Sandbox (Zero Production Impact)</h3>
              <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                Spin up a temporary disposable database, apply forward migration, verify rollback, and destroy
              </span>
            </div>
            <div className="saas-card-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8125rem', marginBottom: '0.35rem', color: '#0f172a' }}>
                    1. Forward Migration SQL:
                  </label>
                  <textarea
                    rows="5"
                    className="saas-search-input"
                    style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.8125rem', resize: 'vertical' }}
                    value={dryRunSql.migrationSql}
                    onChange={(e) => setDryRunSql(prev => ({ ...prev, migrationSql: e.target.value }))}
                    disabled={dryRunLoading}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8125rem', marginBottom: '0.35rem', color: '#0f172a' }}>
                    2. Rollback Verification SQL:
                  </label>
                  <textarea
                    rows="5"
                    className="saas-search-input"
                    style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.8125rem', resize: 'vertical' }}
                    value={dryRunSql.rollbackSql}
                    onChange={(e) => setDryRunSql(prev => ({ ...prev, rollbackSql: e.target.value }))}
                    disabled={dryRunLoading}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                  🛡️ Both steps execute in an isolated sandbox. Production data is never touched.
                </span>
                <button
                  className="saas-btn saas-btn-primary"
                  onClick={handleExecuteDryRun}
                  disabled={dryRunLoading}
                >
                  {dryRunLoading ? '⏳ Running Disposable Sandbox...' : '🚀 Test Migration Dry-Run'}
                </button>
              </div>

              {/* Dry-run Result Banner */}
              {dryRunResult && (
                <div style={{
                  marginTop: '1.25rem',
                  padding: '1rem',
                  borderRadius: '6px',
                  background: dryRunResult.success ? '#f0fdf4' : '#fef2f2',
                  border: `1px solid ${dryRunResult.success ? '#86efac' : '#fecaca'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <strong style={{ color: dryRunResult.success ? '#166534' : '#991b1b' }}>
                      {dryRunResult.success ? '✅ Disposable Dry-Run Passed & Rollback Verified!' : '❌ Dry-Run Migration Failed'}
                    </strong>
                    {dryRunResult.elapsedMilliseconds != null && (
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        Latency: {dryRunResult.elapsedMilliseconds} ms
                      </span>
                    )}
                  </div>
                  {dryRunResult.success ? (
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: '#166534' }}>
                      Forward DDL executed cleanly on temporary disposable database. Automatic rollback verified without lingering constraints or artifacts.
                    </p>
                  ) : (
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: '#b91c1c' }}>
                      {dryRunResult.errorMessage || 'Syntax or constraint error during dry-run execution.'}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual Resolution Modal */}
      {resolvingIncident && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#ffffff', borderRadius: '8px', padding: '1.75rem',
            width: '100%', maxWidth: '520px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '0.75rem', color: '#0f172a' }}>
              Resolve Incident #{resolvingIncident.incidentId} ({resolvingIncident.tenantCode})
            </h3>
            
            {/* Health Status Disclaimer */}
            <div style={{
              background: '#fffbeb', border: '1px solid #fef3c7', padding: '0.75rem',
              borderRadius: '6px', fontSize: '0.8125rem', color: '#92400e', marginBottom: '1rem'
            }}>
              ⚠️ <strong>Clinical Reliability Guard:</strong> Marking this operational incident resolved acknowledges that operators have addressed the issue. <em>It does NOT mark the tenant database healthy.</em> Tenant health status is exclusively updated by subsequent successful health check pings.
            </div>

            {resolutionError && (
              <div style={{ color: '#dc2626', fontSize: '0.8125rem', marginBottom: '0.75rem' }}>
                {resolutionError}
              </div>
            )}

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>
                Operator Resolution Comment (Mandatory):
              </label>
              <textarea
                rows="3"
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.875rem' }}
                placeholder="Describe actions taken (e.g. database credentials corrected, firewall rule added, schema drift verified)..."
                value={resolutionComment}
                onChange={(e) => setResolutionComment(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                className="saas-btn saas-btn-secondary"
                onClick={() => setResolvingIncident(null)}
                disabled={actionLoading != null}
              >
                Cancel
              </button>
              <button
                className="saas-btn saas-btn-success"
                onClick={handleConfirmResolve}
                disabled={actionLoading != null}
              >
                {actionLoading != null ? '⏳ Resolving...' : 'Confirm Incident Resolution'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformHealth;
